import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import * as React from "react"
import { $isHeadingNode } from "@lexical/rich-text"
import { $setBlocksType } from "@lexical/selection"
import cs from "classnames"
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  LexicalNode,
} from "lexical"
import { useCallback, useEffect, useState } from "react"
import { $isLinkNode, LinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link"
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list"
import { $getNearestNodeOfType } from "@lexical/utils"
import { LinkOutlined } from "@mui/icons-material"
import { Autocomplete, Popper, TextField } from "@mui/material"
import MuiThemeProvider from "@web/components/Forms/MuiThemeProvider"
import { Option } from "@web/components/Forms/types"
import { Divider } from "./Divider"
import { FloatingLinkEditor } from "./FloatingLinkEditor/FloatingLinkEditor"
import { InitialTextPlugin } from "./InitialTextPlugin"
import { OptionSet } from "./OptionSet"
import styles from "./ToolbarPlugin.module.scss"
import {
  BlockType,
  BlockTypeConstructorMapping,
  BlockTypeOptions,
  TextAlignment,
  TextAlignmentOptions,
  TextFormat,
  TextFormatOptions,
  getBlockTypeOption,
  getSelectedNode,
  toggleEditedLinkVisibility,
} from "./utils"
import { VariableSelector } from "./VariablesPlugin/VariableSelector"
import { ImageInput } from "./ImageInput"

interface ToolbarPluginProps {
  initialText?: string
  imitateTyping?: boolean
  isHidden?: boolean
  allowCreateVariables?: boolean
  allowSetVariableValues?: boolean
  linkEditorClassName?: string
  withImages?: boolean

  onBlur?: () => void
  setIsTyping: (v: boolean) => void

  /**
   * Will be called instead of internal variable insertion logic.
   */
  onVariableSelected?: (value: string) => void
  onVariableMenuOpen?: () => void
  onVariableMenuClose?: () => void
}

export const ToolbarPlugin = ({
  initialText,
  isHidden,
  setIsTyping,
  imitateTyping,
  allowCreateVariables,
  allowSetVariableValues,
  linkEditorClassName,
  withImages,
  onBlur,
  onVariableSelected,
  onVariableMenuOpen,
  onVariableMenuClose,
}: ToolbarPluginProps) => {
  const [editor] = useLexicalComposerContext()

  const [blockType, setBlockType] = useState<Option<BlockType>>(getBlockTypeOption("rgpt-paragraph"))
  const [textFormat, setTextFormat] = useState<TextFormat[]>([])
  const [linkPopupRoot, setLinkPopupRoot] = useState<{
    el: HTMLElement
    initialUrl?: string
    initialText?: string
    node: LinkNode
  } | null>(null)

  const handleBlockTypeSelected = useCallback(
    (_: unknown, option: Option<BlockType>) => {
      setBlockType(option)

      if (["ol", "ul"].includes(option.value)) {
        switch (option.value) {
          case blockType.value: // remove list if the same list was selected
            return editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)

          case "ol":
            return editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)

          case "ul":
            return editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
        }
        return
      }

      editor.update(() => {
        const selection = $getSelection()

        if (!$isRangeSelection(selection)) return

        const blockTypeConstructor = BlockTypeConstructorMapping[option.value]

        if (!blockTypeConstructor) return

        $setBlocksType(selection, blockTypeConstructor)
      })
    },
    [editor, blockType.value]
  )

  const applyInlineStyles = () => {
    const allElements = document.querySelectorAll<HTMLElement>('[data-lexical-text="true"')

    allElements.forEach((element) => {
      const shouldBeBold = element.classList.contains("editor-text-bold")
      const shouldBeItalic = element.classList.contains("editor-text-italic")
      const shouldBeUnderline = element.classList.contains("editor-text-underline")
      const shouldBeStrikeThrough = element.classList.contains("editor-text-strikethrough")

      element.style.fontWeight = shouldBeBold ? "bold" : ""
      element.style.fontStyle = shouldBeItalic ? "italic" : ""
      element.style.textDecoration =
        !shouldBeStrikeThrough && !shouldBeUnderline
          ? "none"
          : `${shouldBeUnderline ? "underline" : ""} ${shouldBeStrikeThrough ? "line-through" : ""}`
    })
  }

  const handleTextFormatSelected = useCallback(
    (fontDecoration: TextFormat) => () => {
      setTextFormat((c) =>
        c.includes(fontDecoration) ? c.filter((fd) => fd !== fontDecoration) : [fontDecoration, ...c]
      )

      editor.dispatchCommand(FORMAT_TEXT_COMMAND, fontDecoration)
    },
    [editor]
  )

  const handleTextAlignmentSelected = useCallback(
    (textAlignment: TextAlignment) => () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, textAlignment),
    [editor]
  )

  const makeLinkEditable = useCallback(
    (linkNode?: LexicalNode, shouldAutoOpenEditor = true) => {
      editor.update(() => {
        // If no node is passed - use current selection
        if (!linkNode) {
          const selection = $getSelection()
          if (!$isRangeSelection(selection)) return
          linkNode = getSelectedNode(selection)

          // For some reason if the selection includes the beginning of the paragraph node,
          // lexical grabs the <span /> inside of <a /> instead of <a />,
          // next line makes sure we work with <a />.
          if (!$isLinkNode(linkNode) && linkNode) linkNode = linkNode.getParent() as LexicalNode
        }

        if ($isLinkNode(linkNode)) {
          const nodeElement = editor.getElementByKey(linkNode.__key)!

          const openLinkEditor = () => {
            if (!nodeElement.textContent?.trim()) {
              toggleEditedLinkVisibility({ element: nodeElement, visible: false })
            }

            setLinkPopupRoot({
              el: nodeElement,
              initialText: nodeElement.textContent?.trim() || "",
              initialUrl: nodeElement.getAttribute("href") || "",
              node: linkNode as LinkNode,
            })
          }

          nodeElement?.addEventListener("click", openLinkEditor)

          if (shouldAutoOpenEditor) {
            openLinkEditor()
          }
        }
      })
    },
    [editor]
  )

  const insertLink = useCallback(() => {
    // Insert an empty `<a/>` tag
    editor.update(() => {
      const selection = $getSelection()

      if (!$isRangeSelection(selection)) return

      const isLink = $isLinkNode(getSelectedNode(selection).getParent())

      // Lexical needs at least some selection range to insert <a/> tag, so
      // if selection has 0 length we insert a space character and select it.
      if (!selection.getTextContent() && !isLink) {
        const emptyLinkNode = $createTextNode(" ")
        selection.insertNodes([emptyLinkNode])
        selection.setTextNodeRange(emptyLinkNode, 0, emptyLinkNode, 1)
      }

      if (!isLink) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, "")
      }
    })
    // Wait until `<a/>` is inserted in lexical, then auto-open editor and allow opening it on click
    requestAnimationFrame(() => makeLinkEditable())
  }, [editor, makeLinkEditable])

  // If user has moved the cursor to a text block with different formatting,
  // we'll want to update toolbar state to reflect new cursor position.
  const updateToolbar = useCallback(() => {
    const selection = $getSelection()

    if (!$isRangeSelection(selection)) return

    const anchorNode = selection.anchor.getNode()
    const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow()
    const elementKey = element.getKey()
    const elementDOM = editor.getElementByKey(elementKey)

    if (elementDOM !== null) {
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode)
        const type = (parentList ? parentList.getTag() : element.getTag()) as BlockType

        setBlockType(getBlockTypeOption(type))
      } else {
        let type = ($isHeadingNode(element) ? element.getTag() : element.getType()) as BlockType

        if (type === "root") type = "rgpt-paragraph"

        setBlockType(getBlockTypeOption(type))
      }
    }

    const textFormat: TextFormat[] = []

    // Update text format
    selection.hasFormat("bold") && textFormat.push("bold")
    selection.hasFormat("italic") && textFormat.push("italic")
    selection.hasFormat("underline") && textFormat.push("underline")
    selection.hasFormat("strikethrough") && textFormat.push("strikethrough")

    setTextFormat(textFormat)

    // Wait until lexical applies formatting classnames to the element
    requestAnimationFrame(() => applyInlineStyles())
  }, [editor])

  // Register editor state listeners
  useEffect(
    () => editor.registerUpdateListener(({ editorState }) => editorState.read(updateToolbar)),
    [editor, updateToolbar]
  )

  const handleLinkAutoInsert = useCallback(() => makeLinkEditable(undefined, false), [makeLinkEditable])

  const handleLinkEditingDone = useCallback(() => {
    if (!linkPopupRoot) return

    toggleEditedLinkVisibility({ element: linkPopupRoot.el, visible: true })
    setLinkPopupRoot(null)
  }, [linkPopupRoot])

  const [isAddingImage, setIsAddingImage] = useState(false)

  return (
    <MuiThemeProvider>
      <InitialTextPlugin
        imitateTyping={imitateTyping}
        setIsTyping={setIsTyping}
        initialText={initialText}
        makeLinkEditable={makeLinkEditable}
        enableVariables={!!allowCreateVariables || !!allowSetVariableValues}
        onBlur={onBlur}
      />

      {/* Link editing should be fixed for the auto-link plugin before enabling */}
      {/* <AutoLinkPlugin onLinkInsert={handleLinkAutoInsert} /> */}

      <div className={cs(styles.root, { [styles.hidden]: isHidden })}>
        <div className={styles.section}>
          <Autocomplete<Option<BlockType>, false, true>
            options={BlockTypeOptions}
            renderInput={(p) => <TextField {...p} />}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            disableClearable
            onChange={handleBlockTypeSelected}
            value={blockType}
            disablePortal
          />
        </div>

        <Divider />

        <div className={styles.section}>
          <OptionSet<TextFormat>
            options={TextFormatOptions}
            selectedOptions={textFormat}
            handleOptionSelected={handleTextFormatSelected}
          />
        </div>

        <Divider />

        <div className={styles.section}>
          <OptionSet<TextAlignment> options={TextAlignmentOptions} handleOptionSelected={handleTextAlignmentSelected} />
        </div>

        <Divider />

        <div className={styles.section}>
          <button
            tabIndex={-1}
            onClick={insertLink}
            className={cs(styles.toolbarButton, { [styles.active]: false })}
            type="button"
          >
            <LinkOutlined />
          </button>
        </div>

        {allowCreateVariables && (
          <>
            <Divider />
            <div className={styles.section}>
              <VariableSelector
                onVariableSelected={onVariableSelected}
                onVariableMenuOpen={onVariableMenuOpen}
                onVariableMenuClose={onVariableMenuClose}
              />
            </div>
          </>
        )}

        {withImages && (
          <>
            <Divider />
            <div className={styles.section}>
              <ImageInput editor={editor} />
            </div>
          </>
        )}
      </div>

      <Popper
        open={!!linkPopupRoot}
        anchorEl={linkPopupRoot?.el}
        className={styles.linkEditorPopper}
        keepMounted={false}
      >
        {linkPopupRoot && (
          <FloatingLinkEditor
            onDone={handleLinkEditingDone}
            initialUrl={linkPopupRoot?.initialUrl}
            initialText={linkPopupRoot?.initialText}
            node={linkPopupRoot.node}
            element={linkPopupRoot.el}
            className={linkEditorClassName}
          />
        )}
      </Popper>
    </MuiThemeProvider>
  )
}
